import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { AiSearchResponse } from "../../../../api/graphql";
import { AiSearchResponseListStore } from "../../store";
import { aiSearchConversationDisplayId } from "../../../aiSearchConversation/store/AiSearchConversationCommonStore";
import { userDisplayId } from "../../../user/store/UserCommonStore";
import { aiSearchToolCallDisplayId } from "../../../aiSearchToolCall/store/AiSearchToolCallCommonStore";

import {
  VisibilityIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
} from "../../../../icons";

export type GridExpressProps = { listStore?: AiSearchResponseListStore };

export const AiSearchResponseListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<AiSearchResponse>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: AiSearchResponse, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: AiSearchResponse) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: AiSearchResponse, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<AiSearchResponse>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "conversation") {
          return {
            ...col,
            id: "conversation",
            accessorFn: (row: AiSearchResponse) =>
              row.conversation
                ? aiSearchConversationDisplayId(row.conversation, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "createdAt") {
          return {
            ...col,
            id: "createdAt",
            accessorFn: (row: AiSearchResponse) =>
              row.createdAt
                ? new Date(row.createdAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "rating") {
          return {
            ...col,
            id: "rating",
            accessorFn: (row: AiSearchResponse) => {
              const v = (row as any).rating;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.EvaluationRatingOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "evaluatedAt") {
          return {
            ...col,
            id: "evaluatedAt",
            accessorFn: (row: AiSearchResponse) =>
              row.evaluatedAt
                ? new Date(row.evaluatedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "evaluatedBy") {
          return {
            ...col,
            id: "evaluatedBy",
            accessorFn: (row: AiSearchResponse) =>
              row.evaluatedBy ? userDisplayId(row.evaluatedBy, langStore) : "-",
          };
        }
        if (col.accessorKey === "toolCalls") {
          return {
            ...col,
            id: "toolCalls",
            accessorFn: (row: AiSearchResponse) =>
              row.toolCalls?.length
                ? row.toolCalls
                    .map((x: any) => aiSearchToolCallDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.aiSearchResponseCurrentPage || [];

    return (
      <div id={"ListOfAiSearchResponse"}>
        <div id={"StartOfListingOfAiSearchResponse"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
